import * as React from "react";
import {
  BackIcon,
  Button,
  ButtonColor,
  ButtonVariant,
  IconButton,
  Text,
  TextColor,
  TextVariant,
  TopBar,
  TopBarPosition,
} from "../ui";
import { Layout } from "../Layout";
import { LoadingMessage } from "../VehicleHistory/LoadingMessage";
import { RetryButton } from "../VehicleHistory/RetryButton";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { PageLoadStatus } from "../../lib/pageLoadStatus.utils";
import { FadeIn } from "../FadeIn";

// Most loads finish sooner, so they show only the header instead of flashing a loader.
export const LOADER_DELAY_MS = 300;

/** A record page before it has its record: the header and nav stay put while it loads or fails. */
export const PageLoadState: React.FC<{
  status: PageLoadStatus;
  /** App bar title, e.g. "Job". */
  title: string;
  /** Lower-case name of the record, e.g. "job". */
  noun: string;
  onBack: () => void;
  onRetry: () => Promise<unknown> | void;
  loaderDelayMs?: number;
}> = ({ status, title, noun, onBack, onRetry, loaderDelayMs = LOADER_DELAY_MS }) => {
  const loading = status === PageLoadStatus.LOADING;
  const [showLoader, setShowLoader] = React.useState(loading && loaderDelayMs <= 0);
  React.useEffect(() => {
    if (!loading) return setShowLoader(false);
    const timer = setTimeout(() => setShowLoader(true), loaderDelayMs);
    return () => clearTimeout(timer);
  }, [loading, loaderDelayMs]);
  const isOnline = useOnlineStatus();
  const backButton = (
    <Button
      variant={ButtonVariant.TEXT}
      color={ButtonColor.PRIMARY}
      onClick={onBack}
      style={{ minHeight: 48, minWidth: 160, textTransform: "none", fontWeight: 600 }}
    >
      Go back
    </Button>
  );

  return (
    <Layout
      appBar={
        <TopBar position={TopBarPosition.STICKY}>
          <IconButton onClick={onBack} aria-label="Back">
            <BackIcon />
          </IconButton>
          <Text variant={TextVariant.H6}>{title}</Text>
        </TopBar>
      }
    >
      <div className="flex flex-col items-center gap-3 px-4 pt-12 pb-6 text-center">
        {loading && showLoader && (
          <FadeIn>
            <LoadingMessage variant={TextVariant.BODY1}>{`Loading ${noun}…`}</LoadingMessage>
          </FadeIn>
        )}
        {status === PageLoadStatus.NOT_FOUND && (
          <div role="alert" className="flex flex-col items-center gap-3">
            <Text variant={TextVariant.H6}>{`This ${noun} wasn't found`}</Text>
            <Text variant={TextVariant.BODY2} color={TextColor.SECONDARY}>
              It may have been deleted, or the link is out of date.
            </Text>
            {backButton}
          </div>
        )}
        {status === PageLoadStatus.FAILED && (
          <div role="alert" className="flex flex-col items-center gap-3">
            <Text variant={TextVariant.H6}>{`Couldn't load this ${noun}`}</Text>
            <Text variant={TextVariant.BODY2} color={isOnline ? TextColor.SECONDARY : TextColor.ERROR}>
              {isOnline ? "Check your signal and try again." : "This phone is offline. Reconnect and try again."}
            </Text>
            {/* RetryButton is shared with the history; on a full page it gets a wider tap target. */}
            <div className="flex justify-center">
              <RetryButton onRetry={onRetry} minWidth={160} />
            </div>
            {backButton}
          </div>
        )}
      </div>
    </Layout>
  );
};
